import { useState } from 'react'
import { format, subDays } from 'date-fns'
import { LuSearch } from 'react-icons/lu'
import { useAuth } from '../context/AuthContext'
import api from '../services/api'
import './PortalTools.css'

function pick(obj, keys) {
  if (!obj || typeof obj !== 'object') return null
  for (const key of keys) {
    const value = obj[key]
    if (value !== undefined && value !== null && String(value).trim() !== '') return value
  }
  return null
}

function extractRows(payload) {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  for (const key of ['transfers', 'items', 'results', 'rows', 'outgoing_transfers', 'data']) {
    if (Array.isArray(payload[key])) return payload[key]
  }
  if (payload.data && typeof payload.data === 'object') return extractRows(payload.data)
  return []
}

function formatFecha(value) {
  if (value == null || String(value).trim() === '') return '-'
  const raw = String(value).trim()
  const iso = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return raw
  return new Intl.DateTimeFormat('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date)
}

function mapRow(row, index) {
  return {
    key: pick(row, ['id', 'process_id', 'processId', 'origin_id']) || `row-${index}`,
    fecha: formatFecha(pick(row, ['fecha', 'created_at', 'event_timestamp', 'datetime', 'timestamp', 'date', 'executed_at'])),
    origen: pick(row, ['origen', 'source']) || '-',
    usuario: pick(row, [
      'usuario',
      'usuario_nombre',
      'executed_by_full_name',
      'client_name',
      'executed_by_username',
      'username'
    ]) || '-',
    cvu_origen: pick(row, ['cvu_origen', 'from_cvu', 'origin_cvu']) || '-',
    cvu_destino: pick(row, ['cvu_destino', 'to_cvu', 'to_cbu', 'destination_cvu', 'destination_cbu_cvu']) || '-',
    titular_destino: pick(row, ['titular_destino', 'destination_name', 'to_cardholder', 'cardholder_to', 'destinatario_nombre']) || '-',
    monto: pick(row, ['monto', 'amount']),
    process_id: pick(row, ['process_id', 'processId', 'origin_id']) || '-'
  }
}

function accountCvu(account) {
  return String(account?.cvu || '').trim()
}

function AuditoriaPage() {
  const { assignedAccounts } = useAuth()
  const accounts = Array.isArray(assignedAccounts) ? assignedAccounts : []
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'))
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [q, setQ] = useState('')
  const [source, setSource] = useState('')
  const [cvu, setCvu] = useState('')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searched, setSearched] = useState(false)
  const cvusForQuery = (selectedCvu) => {
    if (selectedCvu) return selectedCvu
    const list = accounts.map(accountCvu).filter((value) => /^\d{22}$/.test(value)).slice(0, 70)
    return list.length ? list.join(',') : undefined
  }

  const handleSearch = async (e) => {
    if (e) e.preventDefault()
    const form = e?.currentTarget
    const from = form?.elements?.from_date?.value || fromDate
    const to = form?.elements?.to_date?.value || toDate
    const selectedCvu = form?.elements?.cvu?.value ?? cvu
    const selectedSource = form?.elements?.source?.value ?? source
    const query = (form?.elements?.q?.value ?? q).trim()

    setFromDate(from)
    setToDate(to)
    setLoading(true)
    setError('')
    setSearched(true)
    try {
      const response = await api.get('/transfer-execution-audit/outgoing-v2', {
        timeout: 90000,
        params: {
          from_date: from,
          to_date: to,
          q: query || undefined,
          source: selectedSource || undefined,
          cvus: cvusForQuery(selectedCvu),
          limit: 200,
          offset: 0,
          sort_by: 'fecha',
          sort: 'desc'
        }
      })
      const body = response.data || {}
      const data = body.data && !Array.isArray(body.data) ? body.data : body
      const list = extractRows(data).map(mapRow)
      const rawTotal = data.total ?? data.pagination?.total ?? body.total
      const totalCount = Number(rawTotal)
      setRows(list)
      setTotal(Number.isFinite(totalCount) ? totalCount : list.length)
    } catch (err) {
      setRows([])
      setTotal(0)
      setError(err.response?.data?.error || 'No se pudo cargar la auditoría')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Auditoría</h1>
        <p>Transferencias salientes de tus CVUs, del portal y de la API.</p>
      </div>

      <form className="tool-card tool-filters" onSubmit={handleSearch}>
        <div>
          <label>Desde</label>
          <input type="date" name="from_date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label>Hasta</label>
          <input type="date" name="to_date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div>
          <label>Origen</label>
          <select className="form-control" name="source" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Todos</option>
            <option value="portal">Portal</option>
            <option value="api">API</option>
          </select>
        </div>
        <div>
          <label>Cuenta</label>
          <select className="form-control" name="cvu" value={cvu} onChange={(e) => setCvu(e.target.value)}>
            <option value="">Todas</option>
            {accounts.map((account) => (
              <option key={account.cvu || account.id} value={account.cvu}>
                {account.label || account.alias || account.account_number || account.cvu}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Buscar</label>
          <input className="form-control" name="q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Usuario, CVU, titular..." />
        </div>
        <button className="btn-primary" disabled={loading}><LuSearch /> {loading ? 'Buscando...' : 'Buscar'}</button>
      </form>

      {error && <div className="alert alert-error">{error}</div>}

      {searched && !loading && rows.length === 0 && !error && (
        <div className="empty-state">
          <p>No hay transferencias en el período.</p>
          {accounts.length === 0 && (
            <p>Esta consulta solo devuelve salientes de los CVUs asignados al usuario. Si no tiene cuentas, el listado llega vacío.</p>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Origen</th>
                <th>Usuario</th>
                <th>CVU origen</th>
                <th>CVU destino</th>
                <th>Titular</th>
                <th>Monto</th>
                <th>Process ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`${row.key}-${i}`}>
                  <td>{row.fecha}</td>
                  <td>{row.origen}</td>
                  <td>{row.usuario}</td>
                  <td className="mono">{row.cvu_origen}</td>
                  <td className="mono">{row.cvu_destino}</td>
                  <td>{row.titular_destino}</td>
                  <td>{row.monto != null ? `$ ${Number(row.monto).toLocaleString('es-AR')}` : '-'}</td>
                  <td className="mono">{row.process_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="tool-total">{total} transferencia{total === 1 ? '' : 's'}</p>
        </div>
      )}
    </div>
  )
}

export default AuditoriaPage
